'use client';

import React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import Switch from '@cineamo/ui/src/switch/Switch';

type PreferenceToggleProps = {
    label: React.ReactNode;
    description?: React.ReactNode;
    checked: boolean;
    disabled?: boolean;
    onChange: (next: boolean) => void;
    /** When true, only the switch renders. The visible label is omitted. */
    hideLabel?: boolean;
};

/**
 * Toggle row used on the Newsletter Preferences page. Composes the shared
 * `<Switch>` from `@cineamo/ui` for consistent visuals; renders an optional
 * label + description to the right.
 */
export default function PreferenceToggle({
    label,
    description,
    checked,
    disabled = false,
    onChange,
    hideLabel = false
}: PreferenceToggleProps) {
    const SwitchEl = (
        <Switch
            state={checked}
            disabled={disabled}
            onChange={onChange}
            onColour="bg-[var(--primary-color)]"
            offColour="bg-[rgba(var(--on-background-color-rgb),0.2)]"
            switchColour="bg-[var(--on-primary-color)]"
        />
    );

    if (hideLabel) return SwitchEl;

    return (
        <div className="flex items-start gap-16">
            {SwitchEl}
            <div
                className={cn(
                    'flex flex-col gap-4 cursor-pointer select-none',
                    disabled && 'cursor-not-allowed opacity-50'
                )}
                onClick={() => !disabled && onChange(!checked)}>
                {label !== '' && <span className="text-body-bold-14 text-[var(--on-background-color)]">{label}</span>}
                {description && (
                    <span className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.5)]">{description}</span>
                )}
            </div>
        </div>
    );
}
